import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const CSS_Backgrounds = () => {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">🎨 CSS Backgrounds</h1>

      <p className="tutorial-intro">
        CSS provides several properties to style the background of elements.
        Backgrounds can be <strong>colors</strong>, <strong>images</strong>,{" "}
        <strong>gradients</strong>, and more. Let's explore them step by step.
      </p>

      {/* Background Color */}
      <div className="tutorial-section">
        <h2 className="section-title">1️⃣ Background Color</h2>
        <p>
          The <code>background-color</code> property sets the background color
          of an element.
        </p>
        <CodeCopyBlock
          code={`div {
  background-color: lightblue;
}`}
        />
        <div
          style={{
            backgroundColor: "lightblue",
            padding: "1rem",
            borderRadius: "8px",
            marginTop: "1rem",
          }}
        >
          This box has a <strong>light blue</strong> background.
        </div>
      </div>

      {/* Background Image */}
      <div className="tutorial-section">
        <h2 className="section-title">2️⃣ Background Image</h2>
        <p>
          Use the <code>background-image</code> property to set an image as the
          background.
        </p>
        <CodeCopyBlock
          code={`div {
  background-image: url("background.jpg");
}`}
        />
        <div
          style={{
            backgroundImage: "url('https://picsum.photos/400/150?random=1')",
            padding: "1.5rem",
            borderRadius: "8px",
            marginTop: "1rem",
            color: "white",
            fontWeight: "bold",
          }}
        >
          This box has a background image.
        </div>
      </div>

      {/* Background Repeat */}
      <div className="tutorial-section">
        <h2 className="section-title">3️⃣ Background Repeat</h2>
        <p>
          The <code>background-repeat</code> property defines whether the
          background image repeats.
        </p>
        <CodeCopyBlock
          code={`div {
  background-image: url("pattern.png");
  background-repeat: repeat-x;
}`}
        />
        <div
          style={{
            backgroundImage:
              "url('https://www.transparenttextures.com/patterns/cubes.png')",
            backgroundRepeat: "repeat-x",
            padding: "2rem",
            borderRadius: "8px",
            marginTop: "1rem",
          }}
        >
          This background repeats <strong>horizontally</strong>.
        </div>
      </div>

      {/* Background Position */}
      <div className="tutorial-section">
        <h2 className="section-title">4️⃣ Background Position</h2>
        <p>
          The <code>background-position</code> property defines the starting
          position of a background image.
        </p>
        <CodeCopyBlock
          code={`div {
  background-image: url("logo.png");
  background-repeat: no-repeat;
  background-position: center;
}`}
        />
        <div
          style={{
            backgroundImage:
              "url('https://upload.wikimedia.org/wikipedia/commons/a/a7/React-icon.svg')",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            backgroundSize: "80px",
            padding: "4rem",
            borderRadius: "8px",
            border: "1px solid #ddd",
            marginTop: "1rem",
          }}
        >
          Background positioned at <strong>center</strong>.
        </div>
      </div>

      {/* Background Size */}
      <div className="tutorial-section">
        <h2 className="section-title">5️⃣ Background Size</h2>
        <p>
          The <code>background-size</code> property specifies how the background
          image should be scaled.
        </p>
        <CodeCopyBlock
          code={`div {
  background-image: url("nature.jpg");
  background-size: cover;
}`}
        />
        <div
          style={{
            backgroundImage: "url('https://picsum.photos/400/200?random=2')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            height: "150px",
            borderRadius: "8px",
            marginTop: "1rem",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: "bold",
          }}
        >
          Background size set to <strong>cover</strong>.
        </div>
      </div>

      {/* Background Gradient */}
      <div className="tutorial-section">
        <h2 className="section-title">6️⃣ Background Gradient</h2>
        <p>
          Gradients are smooth transitions between colors. They can be linear or
          radial.
        </p>
        <CodeCopyBlock
          code={`div {
  background: linear-gradient(to right, #4facfe, #00f2fe);
}`}
        />
        <div
          style={{
            background: "linear-gradient(to right, #4facfe, #00f2fe)",
            padding: "2rem",
            borderRadius: "8px",
            marginTop: "1rem",
            color: "white",
            fontWeight: "bold",
            textAlign: "center",
          }}
        >
          Linear Gradient Background
        </div>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">🚀 Next Steps</h2>
        <p>
          With backgrounds, you can make your web pages visually engaging. Next,
          explore{" "}
          <a href="/css/colors" className="learn-more">
            CSS Colors
          </a>{" "}
          to learn how to control the look and feel of text and elements in
          detail.
        </p>
      </div>
    </div>
  );
};

export default CSS_Backgrounds;
